import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { useRouterStore } from 'mobx-state-router';
import React, { useCallback, useContext, useState } from 'react';

import { Alert, Button } from 'src/common/ui/components';
import { SideVisitContext } from 'src/components/pages/eventShow/SideVisitContext';
import { ApolloClientContext } from 'src/data/ApolloClientContext';
import { ACCEPT_MUTATION } from 'src/dropInClinic/queries';
import { AcceptMutationArgs, AcceptMutationResponse } from 'src/dropInClinic/types';
import { OBSERVABILITY_MY_DAY_TODAY } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { useProviderId } from 'src/myDayToday/domain/useProviderId';
import { useObservability } from 'src/observability/useObservability';

type AcceptSeePatientSuggestionProps = {
  requestId?: string | null;
  disabled: boolean;
};

const AcceptSeePatientSuggestionButton = (props: AcceptSeePatientSuggestionProps) => {
  const { apolloClient } = useContext(ApolloClientContext);
  const { setSideVisit } = useContext(SideVisitContext);
  const [requestUpdateError, setRequestUpdateError] = useState(null);
  const router = useRouterStore();
  const enableObservability = useFeatureFlag(OBSERVABILITY_MY_DAY_TODAY);
  const sendStaffEvent = useObservability(enableObservability);
  const providerId = useProviderId();

  const handleAccept = useCallback(
    async (id: string) => {
      setRequestUpdateError(null);
      if (!apolloClient) {
        return;
      }

      try {
        const response = await apolloClient.mutate<AcceptMutationResponse, AcceptMutationArgs>({
          mutation: ACCEPT_MUTATION,
          variables: { requestId: id },
        });

        sendStaffEvent({
          eventType: 'myDayToday:acceptPopInEventSeePatientNow',
          userId: providerId ?? null,
          details: {
            requestId: id,
          },
        });

        const eventId = response.data?.acceptDropInClinicRequest.eventId;
        const patientId = response.data?.acceptDropInClinicRequest.patientId;
        if (eventId && patientId) {
          // Show the visit note in the right SideVisit pane, then navigate this same tab to the
          // patient page. We don't pin the tab: patientShow's defaultTab lands snapshot-eligible
          // providers on the Clinical Snapshot (left/main column) and falls back to Overview when
          // the CLINICAL_SNAPSHOT flag is off, so the left pane is never blank. We no longer open
          // the video call here — see ORCA-2449.
          setSideVisit(patientId, eventId);
          router.goTo('showPatient', { params: { id: patientId } });
        }
      } catch (e) {
        setRequestUpdateError(e);
      }
    },
    [apolloClient, setRequestUpdateError, router, setSideVisit, sendStaffEvent, providerId],
  );

  return (
    <>
      {requestUpdateError && (
        <Alert title="Error updating request" error={requestUpdateError} severity="error" />
      )}
      <Button
        disabled={props.disabled || !props.requestId}
        onClick={() => props.requestId && handleAccept(props.requestId)}
        startIcon={<PersonAddIcon />}
        data-testid="accept-see-patient-suggestion-button"
      >
        Accept patient now
      </Button>
    </>
  );
};

export default AcceptSeePatientSuggestionButton;
